// SPDX-License-Identifier: AGPL-3.0-or-later

import {WIDE_IMAGE_ASPECT_RATIO_LABEL} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {GuildBoostLevelBadge} from '@app/features/boost/components/GuildBoostLevelBadge';
import chatStyles from '@app/features/channel/components/GuildChatWallpaper.module.css';
import {AssetType, getAssetConfig} from '@app/features/expressions/components/modals/AssetCropModal';
import {getAcceptString} from '@app/features/expressions/utils/AssetFormatCopy';
import {formatImageUploadMinimumHint} from '@app/features/expressions/utils/AssetUploadHintCopy';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/fields/GuildWallpaperField.module.css';
import overviewStyles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import type {GuildLike} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTypes';
import {useGuildImageAssetField} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/hooks/useGuildImageAssetField';
import type {FormInputs} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {int2hex} from '@app/features/theme/utils/ColorUtils';
import {Button} from '@app/features/ui/button/Button';
import {ColorPickerField} from '@app/features/ui/components/form/ColorPickerField';
import {getGuildWallpaperURL} from '@app/features/user/utils/AvatarUtils';
import {formatAssetUploadExtensions, GUILD_WALLPAPER_SHAPE} from '@fluxer/constants/src/AssetFormatPolicy';
import {GUILD_WALLPAPER_MAX_SIZE} from '@fluxer/constants/src/LimitConstants';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {useWatch} from 'react-hook-form';

const GUILD_WALLPAPER_MIN_SIZE = {width: GUILD_WALLPAPER_SHAPE.width.min, height: GUILD_WALLPAPER_SHAPE.height.min};

const WALLPAPER_DESCRIPTOR = msg({
	message: 'Wallpaper',
	comment: 'Label for the community wallpaper in the error modals of the image upload. Keep it concise.',
});
const WALLPAPERS_CANNOT_BE_ANIMATED_DESCRIPTOR = msg({
	message: 'Wallpapers cannot be animated. Use a still image.',
	comment: 'Error message in the community wallpaper field.',
});
const NO_COLOR_SELECTED_DESCRIPTOR = msg({
	message: 'No color selected',
	comment: 'Helper text under the color picker of the community wallpaper while no color is set.',
});
const WALLPAPER_COLOR_DESCRIPTOR = msg({
	message: 'Color',
	comment: 'Label of the color picker in the community wallpaper field. Keep it concise.',
});

export const GuildWallpaperField: React.FC<{
	guild: GuildLike;
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	previewWallpaperUrl: string | null;
	setPreviewWallpaperUrl: React.Dispatch<React.SetStateAction<string | null>>;
	hasClearedWallpaper: boolean;
	setHasClearedWallpaper: React.Dispatch<React.SetStateAction<boolean>>;
}> = observer(
	({
		guild,
		form,
		canManageGuild,
		previewWallpaperUrl,
		setPreviewWallpaperUrl,
		hasClearedWallpaper,
		setHasClearedWallpaper,
	}) => {
		const {i18n} = useLingui();
		const unlocked =
			LimitResolver.resolve({
				key: 'feature_guild_wallpaper',
				fallback: 0,
				context: {guildId: guild.id},
				evaluationContext: 'guild',
			}) > 0;
		const canEdit = canManageGuild && unlocked;
		const config = getAssetConfig(AssetType.GUILD_WALLPAPER);
		const color = useWatch({control: form.control, name: 'wallpaper_color'}) ?? null;
		const controller = useGuildImageAssetField({
			form,
			fieldName: 'wallpaper',
			assetType: AssetType.GUILD_WALLPAPER,
			canManage: canEdit,
			filePickerAccept: getAcceptString('guild_wallpaper'),
			previewUrl: previewWallpaperUrl,
			setPreviewUrl: setPreviewWallpaperUrl,
			setHasCleared: setHasClearedWallpaper,
			maxBytes: GUILD_WALLPAPER_MAX_SIZE,
			minSize: GUILD_WALLPAPER_MIN_SIZE,
			labelForMessages: i18n._(WALLPAPER_DESCRIPTOR),
			gif: {mode: 'disallow', disallowedMessage: i18n._(WALLPAPERS_CANNOT_BE_ANIMATED_DESCRIPTOR)},
		});
		const serverImage = guild.wallpaper ? getGuildWallpaperURL({id: guild.id, wallpaper: guild.wallpaper}) : null;
		const imageUrl = hasClearedWallpaper ? null : (previewWallpaperUrl ?? serverImage);
		const tint = color === null ? undefined : int2hex(color);
		const setColor = (value: number | null) => form.setValue('wallpaper_color', value, {shouldDirty: true});
		return (
			<div className={styles.card} data-locked={unlocked ? undefined : true} data-flx="guild.guild-wallpaper-field">
				<div className={styles.heading} data-flx="guild.guild-wallpaper-field.heading">
					<span className={overviewStyles.iconField}>
						<Trans comment="Heading of the community wallpaper field in the community settings. Keep it concise.">
							Chat wallpaper
						</Trans>
					</span>
					{!unlocked && (
						<span className={styles.lock} data-flx="guild.guild-wallpaper-field.lock">
							<GuildBoostLevelBadge level={3} size={16} />
							<Trans comment="Chip beside a community setting that opens at boost level 3. Keep it concise.">
								Level 3
							</Trans>
						</span>
					)}
				</div>
				<div className={styles.body} data-flx="guild.guild-wallpaper-field.body">
					<div
						className={styles.preview}
						style={tint ? ({'--guild-wallpaper-tint': tint} as React.CSSProperties) : undefined}
						aria-hidden="true"
						data-flx="guild.guild-wallpaper-field.preview"
					>
						<div className={chatStyles.layer} data-tinted={tint ? (imageUrl ? 'image' : 'color') : undefined}>
							{imageUrl && <img className={chatStyles.image} src={imageUrl} alt="" draggable={false} />}
						</div>
						<span className={styles.previewLine} />
						<span className={styles.previewLine} />
						<span className={styles.previewLine} />
					</div>
					<div className={styles.controls} data-flx="guild.guild-wallpaper-field.controls">
						<p className={overviewStyles.imageUploadDescription}>
							<Trans comment="Help text of the community wallpaper field.">
								A still image or a color behind the messages of text channels. It stays dimmed so messages are easy to
								read, and members can turn the image off.
							</Trans>
						</p>
						<div className={overviewStyles.imageUploadButtons}>
							<Button
								variant="primary"
								small={true}
								onClick={controller.openSourcePicker}
								disabled={!canEdit || controller.isProcessing}
								data-flx="guild.guild-wallpaper-field.button.pick-file"
							>
								<Trans comment="Button that opens the file picker for the community wallpaper. Keep it concise.">
									Upload image
								</Trans>
							</Button>
							{imageUrl && (
								<Button
									variant="secondary"
									small={true}
									onClick={controller.clear}
									disabled={!canManageGuild || controller.isProcessing}
									data-flx="guild.guild-wallpaper-field.button.clear"
								>
									<Trans comment="Button that removes the community wallpaper image. Keep it concise.">Remove</Trans>
								</Button>
							)}
						</div>
						<p className={overviewStyles.imageUploadDescription}>
							{unlocked ? (
								formatImageUploadMinimumHint(i18n, {
									formats: formatAssetUploadExtensions('guild_wallpaper'),
									maxSize: formatFileSize(i18n.locale, GUILD_WALLPAPER_MAX_SIZE),
									minimumSize: `${config.minWidth}×${config.minHeight}`,
									aspectRatio: WIDE_IMAGE_ASPECT_RATIO_LABEL,
								})
							) : (
								<Trans comment="Upgrade hint under the disabled upload button of the community wallpaper.">
									Boost to Level 3 to set a chat wallpaper.
								</Trans>
							)}
						</p>
						<ColorPickerField
							label={i18n._(WALLPAPER_COLOR_DESCRIPTOR)}
							description={color === null ? i18n._(NO_COLOR_SELECTED_DESCRIPTOR) : undefined}
							value={color ?? 0}
							defaultValue={0}
							isDefaultValue={color === null}
							onChange={setColor}
							onReset={() => setColor(null)}
							disabled={!canEdit}
							hideHelperText={true}
							data-flx="guild.guild-wallpaper-field.color-picker-field"
						/>
						{color !== null && (
							<div className={overviewStyles.imageUploadButtons}>
								<Button
									variant="secondary"
									small={true}
									onClick={() => setColor(null)}
									disabled={!canManageGuild}
									data-flx="guild.guild-wallpaper-field.button.clear-color"
								>
									<Trans comment="Button that removes the community wallpaper color. Keep it concise.">
										Remove color
									</Trans>
								</Button>
							</div>
						)}
					</div>
				</div>
				{form.formState.errors.wallpaper?.message ? (
					<p className={overviewStyles.errorMessage} data-flx="guild.guild-wallpaper-field.error-message">
						{form.formState.errors.wallpaper.message}
					</p>
				) : null}
			</div>
		);
	},
);
